import * as React from 'react'
import cx from 'classnames'
import { Observable } from 'rxjs'
import Icon from 'components/Icon'
import { useIsElementInView } from 'hooks/viewport-hooks'
import { useObservableCallback } from 'hooks/rx-hooks'
import styles from './Step.sass'

interface StepProps {
  title: string
  descriptionHtml?: string
  children?: React.ReactNode
  isLast?: boolean
  index?: number
  active?: boolean
  isNotValid?: boolean
  becomeActive$: Observable<unknown>
  onBecomeVisible: () => unknown
  onBecomeHidden: () => unknown
  ['data-test']?: string
  compact?: boolean
}

/**
 " Step
 */
export default function Step({
  title,
  descriptionHtml,
  children,
  isLast,
  index,
  active,
  isNotValid,
  becomeActive$,
  onBecomeHidden,
  onBecomeVisible,
  'data-test': dataTest,
  compact,
}: StepProps) {
  const [showingDescription, setShowingDescription] = React.useState(false)
  const toggleDescription = React.useCallback(
    () => setShowingDescription((v) => !v),
    []
  )
  const stepRef = React.useRef<HTMLDivElement | null>(null)
  const scrollIntoView = React.useCallback(
    () => stepRef.current?.scrollIntoView({ block: 'center' }),
    []
  )

  useIsElementInView(stepRef, (isInView) =>
    isInView ? onBecomeVisible() : onBecomeHidden()
  )

  useObservableCallback(becomeActive$, scrollIntoView, [becomeActive$])

  // On first load, if this is the active step, scroll it into view.
  React.useEffect(() => {
    if (active) {
      scrollIntoView()
    }
  }, [])

  return (
    <div className={cx(styles.step, active && styles.active)} ref={stepRef}>
      {index !== undefined && (
        <div className={styles.gutter}>
          <div className={styles.number}>{index + 1}</div>
          {!isLast && <div className={styles.line} />}
        </div>
      )}
      <div className={cx(styles.main, compact && styles.compact)}>
        <div className={styles.info}>
          <h2
            className={cx(
              styles.title,
              compact && styles.compact,
              isNotValid && styles.isNotValid
            )}
            data-test={`${dataTest}-title`}
          >
            {title}
            {descriptionHtml && (
              <>
                <a
                  className={cx(
                    styles.descriptionToggle,
                    isNotValid && styles.isNotValid
                  )}
                  onClick={toggleDescription}
                  data-test={`${dataTest}-description-toggle`}
                >
                  <Icon auto name="info" />
                </a>
              </>
            )}
          </h2>
          {descriptionHtml && showingDescription && (
            <div
              className={cx(
                styles.description,
                compact && styles.compact,
                isNotValid && styles.isNotValid
              )}
              data-test={`${dataTest}-description`}
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          )}
        </div>
        {children && (
          <div className={cx(styles.content, compact && styles.compact)}>
            {children}
          </div>
        )}
      </div>
    </div>
  )
}
